import { Button, createListCollection, HStack, Separator, Text, VStack } from '@chakra-ui/react';
import { useZoneManagementContext } from '@components/cockpit/panels/zoneManagement/ZoneManagementContext';
import {
  DEFAULT_ZONE_FILTERS,
  ZoneActiveStatusEnum,
} from '@components/cockpit/panels/zoneManagement/ZoneManagementFilters.types';
import { ScrollArea } from '@components/ui/ScrollArea';
import { CustomSelect } from '@components/ui/select/Select';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole } from '@redux/authent/authent.selectors';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';

import { RoleEnum } from '@/types/authent/roles.types';
import { ZoneTypeEnum } from '@/types/config/config.types';

const getAccessibleZoneTypes = (canAccessNiz: boolean, canAccessNfz: boolean, canAccessImz: boolean) => {
  const types: ZoneTypeEnum[] = [];
  if (canAccessNiz) {
    types.push(ZoneTypeEnum.NIZ);
  }
  if (canAccessNfz) {
    types.push(ZoneTypeEnum.NFZ, ZoneTypeEnum.NFZ_3D);
  }
  if (canAccessImz) {
    types.push(ZoneTypeEnum.IMZ);
  }
  return types;
};

export function ZoneManagementFilters() {
  const { filters, setFilters, resetFilters } = useZoneManagementContext();
  const { formatMessage } = useIntl();

  const canAccessNiz = useAppSelector((state) => hasRole(state, RoleEnum.ACCESS_ZONE_NIZ));
  const canAccessNfz = useAppSelector((state) => hasRole(state, RoleEnum.ACCESS_ZONE_NFZ));
  const canAccessImz = useAppSelector((state) => hasRole(state, RoleEnum.ACCESS_ZONE_IMZ));

  const canReset = !shallowEqual(filters, DEFAULT_ZONE_FILTERS);

  const types = getAccessibleZoneTypes(canAccessNiz, canAccessNfz, canAccessImz);
  const items = createListCollection({
    items: types.map((value) => ({
      value,
      label: formatMessage({ id: `components.zones.filters.type.${value}` }),
    })),
  });

  const handleToggleStatus = (status: ZoneActiveStatusEnum) => {
    const prevSet = new Set(filters.activeStatus);

    if (prevSet.has(status)) {
      prevSet.delete(status);
    } else {
      prevSet.add(status);
    }

    setFilters({ activeStatus: new Set(prevSet) });
  };

  return (
    <HStack height="100%" gap={0}>
      <VStack gap={0.25} backgroundColor="neutral.black" height="100%" width="400px" flexShrink={0} userSelect="none">
        <HStack
          width="100%"
          height="56px"
          backgroundColor="neutral.850"
          paddingX={2}
          flexShrink={0}
          justifyContent="space-between"
        >
          <Button
            variant="toggle"
            size="toggleS"
            data-active={canReset}
            disabled={!canReset}
            onClick={() => resetFilters()}
          >
            <Text>
              <FormattedMessage id="components.notificationSearch.filters.reset" />
            </Text>
          </Button>
        </HStack>

        <ScrollArea backgroundColor="neutral.850">
          <VStack width="100%" flex={1} gap={0} separator={<Separator borderColor="neutral.900" />}>
            <VStack gap={1.5} width="100%" paddingX={2} paddingY={3} alignItems="start">
              <Text fontWeight="medium" color="neutral.300">
                <FormattedMessage id="components.zones.filters.type.title" />
              </Text>
              <CustomSelect
                collection={items}
                value={filters.type as string}
                clearable={filters.type !== null}
                placeholder={formatMessage({ id: 'components.zones.filters.type.title' })}
                onChange={(value) => setFilters({ type: value as ZoneTypeEnum | null })}
              />
            </VStack>
            <VStack gap={1.5} width="100%" paddingX={2} paddingY={3} alignItems="start">
              <Text fontWeight="medium" color="neutral.300">
                <FormattedMessage id="components.zones.activeStatus" />
              </Text>
              <HStack gap={1} width="100%" height="48px">
                <Button
                  variant="toggle"
                  size="toggleM"
                  flex={1}
                  data-active={filters.activeStatus.has(ZoneActiveStatusEnum.ACTIVATED)}
                  onClick={() => handleToggleStatus(ZoneActiveStatusEnum.ACTIVATED)}
                >
                  <FormattedMessage id="components.zones.activated" />
                </Button>
                <Button
                  variant="toggle"
                  size="toggleM"
                  flex={1}
                  data-active={filters.activeStatus.has(ZoneActiveStatusEnum.DISABLED)}
                  onClick={() => handleToggleStatus(ZoneActiveStatusEnum.DISABLED)}
                >
                  <FormattedMessage id="components.zones.disabled" />
                </Button>
              </HStack>
            </VStack>
          </VStack>
        </ScrollArea>
      </VStack>
    </HStack>
  );
}
